import { AI_TEST_COLLECTION, AI_TEST_PART_LABEL, type Task } from "@xinghai/shared";
import type { IconId } from "@xinghai/ui";
import type { AssignmentItem } from "../../lib/api";
import { LEVEL_SIZE } from "../../lib/constants";
import { etaMinutes, wholeMinutes } from "./taskMeta";

/**
 * A student can hold hundreds of assignments, so 今日任务 shows one
 * "session card" per (batch × task group) instead of one card per assignment.
 * T5 歌词朗读 and T6 演唱 are one group: the pair is always recorded together.
 */
export type TaskGroupKey = Task | "pair_T5_T6";

export function taskGroupKey(task: Task): TaskGroupKey {
  return task === "T5_lyric_reading" || task === "T6_singing" ? "pair_T5_T6" : task;
}

export const GROUP_LABEL: Record<TaskGroupKey, string> = {
  T1_reference: "示范朗读",
  T1b_reference_singing: "示范演唱",
  T2_diagnostic: "单字诊断",
  T3_tongue_twister: "绕口令",
  T4_passage: "段落朗读",
  T5_lyric_reading: "歌词朗读",
  T6_singing: "演唱",
  T7_perception: "听辨",
  pair_T5_T6: "歌词朗读 ↔ 演唱",
};

export const GROUP_ICON: Record<TaskGroupKey, IconId> = {
  T1_reference: "ui-reading",
  T1b_reference_singing: "ui-speaking",
  T2_diagnostic: "ui-hash",
  T3_tongue_twister: "ui-speaking",
  T4_passage: "ui-document",
  T5_lyric_reading: "ui-reading",
  T6_singing: "ui-speaking",
  T7_perception: "ui-listening",
  pair_T5_T6: "ui-speaking",
};

export interface SessionGroup {
  /** `${batch_id}::${groupKey}` — stable across refetches. */
  id: string;
  batchId: string;
  batchTitle: string;
  groupKey: TaskGroupKey;
  label: string;
  icon: IconId;
  /** Assignments in the order the student should record them. */
  items: AssignmentItem[];
  total: number;
  done: number;
  /** The first assignment that is still to do and not locked. */
  firstTodo: AssignmentItem | null;
  /** Minutes left for the unfinished items. */
  etaRemaining: number;
}

/**
 * Recording order inside a pair group: 朗读 then 演唱 of the same pair,
 * then the next pair. Non-pair groups keep the server's order.
 */
function orderItems(items: AssignmentItem[]): AssignmentItem[] {
  const pairOrder: string[] = [];
  for (const a of items) {
    const g = a.pair_group;
    if (g && !pairOrder.includes(g)) pairOrder.push(g);
  }
  return [...items].sort((a, b) => {
    const ga = a.pair_group ? pairOrder.indexOf(a.pair_group) : -1;
    const gb = b.pair_group ? pairOrder.indexOf(b.pair_group) : -1;
    if (ga !== gb) return ga - gb;
    const sa = a.pair_step ?? (a.task === "T6_singing" ? 2 : 1);
    const sb = b.pair_step ?? (b.task === "T6_singing" ? 2 : 1);
    return sa - sb;
  });
}

/** Fold a flat assignment list into session cards. */
export function groupAssignments(items: readonly AssignmentItem[]): SessionGroup[] {
  const map = new Map<string, AssignmentItem[]>();
  const order: string[] = [];
  for (const a of items) {
    const key = `${a.batch_id}::${taskGroupKey(a.task)}`;
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(a);
  }

  return order.map((key) => {
    const raw = map.get(key)!;
    const first = raw[0]!;
    const groupKey = taskGroupKey(first.task);
    const ordered = groupKey === "pair_T5_T6" ? orderItems(raw) : raw;
    const done = ordered.filter((a) => a.state !== "todo").length;
    return {
      id: key,
      batchId: first.batch_id,
      batchTitle: first.batch_title ?? first.batch_id,
      groupKey,
      label: GROUP_LABEL[groupKey],
      icon: GROUP_ICON[groupKey],
      items: ordered,
      total: ordered.length,
      done,
      firstTodo: ordered.find((a) => a.state === "todo" && !a.locked) ?? null,
      etaRemaining: wholeMinutes(
        ordered
          .filter((a) => a.state === "todo")
          .reduce((sum, a) => sum + etaMinutes(a.task, a.eta_minutes), 0),
      ),
    };
  });
}

/** docs/UX.md §1.2 — the six stages of one session, in running order. */
export const SESSION_STAGES: readonly Task[] = [
  "T2_diagnostic",
  "T3_tongue_twister",
  "T4_passage",
  "T5_lyric_reading",
  "T6_singing",
  "T7_perception",
];

export const STAGE_LABEL: Record<string, string> = {
  T2_diagnostic: "单字",
  T3_tongue_twister: "绕口令",
  T4_passage: "段落",
  T5_lyric_reading: "歌词朗读",
  T6_singing: "演唱",
  T7_perception: "听辨",
};

/**
 * 《AI星海正音》(collection `ai_test_v2`) is not a corpus session: the teacher
 * publishes 基础训练 / 辅音正音 / 十三辙归韵 and the student works through one
 * part at a time. Those assignments get one row per part, not the generic task
 * rows. (`level` is the part number 1|2|3.)
 */
export function aiTestLevelOf(a: AssignmentItem): number | null {
  const m = a.material;
  if (!m || m.collection !== AI_TEST_COLLECTION) return null;
  const level = m.level;
  return typeof level === "number" && level > 0 ? level : null;
}

export const AI_TEST_LEVEL_LABEL: Record<number, string> = AI_TEST_PART_LABEL;

/**
 * The teacher's own item heading for an assignment (`17 发花辙 a ·《天净沙·秋思》`),
 * plus where inside that heading the student is — shown as a caption above the
 * material so they always know which 项 they are on.
 */
export function aiTestSectionCaption(
  items: readonly AssignmentItem[],
  assignmentId: string,
): string | null {
  const current = items.find((a) => a.id === assignmentId) ?? null;
  const level = current ? aiTestLevelOf(current) : null;
  if (!current || level === null) return null;
  const section = current.material?.section ?? "";
  if (!section) return null;
  const inSection = orderAiTest(
    items.filter((a) => aiTestLevelOf(a) === level && (a.material?.section ?? "") === section),
  );
  const at = inSection.findIndex((a) => a.id === assignmentId);
  if (inSection.length <= 1 || at < 0) return section;
  return `${section} ${at + 1}/${inSection.length}`;
}

/** Recording order inside a level: the material's authored `sort_key`, then id. */
function orderAiTest(items: AssignmentItem[]): AssignmentItem[] {
  return [...items].sort(
    (a, b) =>
      (a.material?.sort_key ?? 0) - (b.material?.sort_key ?? 0) ||
      a.material_id.localeCompare(b.material_id),
  );
}

export interface StageSummary {
  /** Stable key: the task name, or `ai_test:<level>`. */
  id: string;
  /** `task` rows come from the §2.2 session plan, `ai_test` rows from 《AI 正音测试》. */
  kind: "task" | "ai_test";
  /** For an `ai_test` row this is the task of its first item — only used for icons. */
  task: Task;
  /** 1 | 2 | 3 for an `ai_test` row, null otherwise. */
  level: number | null;
  label: string;
  icon: IconId;
  items: AssignmentItem[];
  total: number;
  done: number;
  /** Minutes left in this stage. */
  etaRemaining: number;
  firstTodo: AssignmentItem | null;
  complete: boolean;
  /** T2 only: which 关 the student is on, 1-based. */
  currentLevel?: number;
  levelCount?: number;
}

/**
 * One row per stage for the home screen. Stages the student has no
 * assignments for are dropped; batch and material ids never surface.
 */
export function stageSummaries(items: readonly AssignmentItem[]): StageSummary[] {
  const out: StageSummary[] = [];

  // 《AI 正音测试》first: one row per level, in the authored order.
  const aiTest = items.filter((a) => aiTestLevelOf(a) !== null);
  const levels = [...new Set(aiTest.map((a) => aiTestLevelOf(a)!))].sort((a, b) => a - b);
  for (const level of levels) {
    const ordered = orderAiTest(aiTest.filter((a) => aiTestLevelOf(a) === level));
    const done = ordered.filter((a) => a.state !== "todo").length;
    out.push({
      id: `ai_test:${level}`,
      kind: "ai_test",
      task: ordered[0]!.task,
      level,
      label: AI_TEST_LEVEL_LABEL[level] ?? `第 ${level} 部分`,
      icon: "ui-hash",
      items: ordered,
      total: ordered.length,
      done,
      etaRemaining: wholeMinutes(
        ordered
          .filter((a) => a.state === "todo")
          .reduce((sum, a) => sum + etaMinutes(a.task, a.eta_minutes), 0),
      ),
      firstTodo: ordered.find((a) => a.state === "todo" && !a.locked) ?? null,
      complete: done >= ordered.length,
    });
  }

  const rest = items.filter((a) => aiTestLevelOf(a) === null);
  for (const task of SESSION_STAGES) {
    const stageItems = rest.filter((a) => a.task === task);
    if (stageItems.length === 0) continue;
    const ordered =
      task === "T6_singing" || task === "T5_lyric_reading" ? orderItems(stageItems) : stageItems;
    const done = ordered.filter((a) => a.state !== "todo").length;
    const firstTodo = ordered.find((a) => a.state === "todo" && !a.locked) ?? null;
    const summary: StageSummary = {
      id: task,
      kind: "task",
      task,
      level: null,
      label: STAGE_LABEL[task] ?? task,
      icon: GROUP_ICON[task] ?? "ui-list",
      items: ordered,
      total: ordered.length,
      done,
      etaRemaining: wholeMinutes(
        ordered
          .filter((a) => a.state === "todo")
          .reduce((sum, a) => sum + etaMinutes(a.task, a.eta_minutes), 0),
      ),
      firstTodo,
      complete: done >= ordered.length,
    };
    if (task === "T2_diagnostic") {
      summary.levelCount = chunkIntoLevels(ordered).length;
      const idx = firstTodo ? ordered.indexOf(firstTodo) : ordered.length - 1;
      summary.currentLevel = Math.floor(Math.max(0, idx) / LEVEL_SIZE) + 1;
    }
    out.push(summary);
  }
  return out;
}

/** The single next thing to do, across all stages, in session order. */
export function nextUp(items: readonly AssignmentItem[]): {
  assignment: AssignmentItem;
  stage: StageSummary;
} | null {
  for (const stage of stageSummaries(items)) {
    if (stage.firstTodo) return { assignment: stage.firstTodo, stage };
  }
  return null;
}

/** Which 关 an assignment sits in (T2), 1-based. */
export function levelOf(stage: StageSummary, assignmentId: string): number {
  const idx = stage.items.findIndex((a) => a.id === assignmentId);
  return idx < 0 ? 1 : Math.floor(idx / LEVEL_SIZE) + 1;
}

export interface LevelChunk {
  /** 1-based. */
  index: number;
  label: string;
  items: AssignmentItem[];
  done: number;
  total: number;
}

/**
 * docs/ROADMAP.md A2 — the 240 单字 are shown as 关 of 30 so the list is
 * navigable; the 继续 button still targets the first todo of the whole group.
 */
export function chunkIntoLevels(items: readonly AssignmentItem[], size = LEVEL_SIZE): LevelChunk[] {
  const chunks: LevelChunk[] = [];
  for (let i = 0; i < items.length; i += size) {
    const slice = items.slice(i, i + size);
    chunks.push({
      index: chunks.length + 1,
      label: `第 ${chunks.length + 1} 关`,
      items: slice,
      done: slice.filter((a) => a.state !== "todo").length,
      total: slice.length,
    });
  }
  return chunks;
}

/**
 * The next assignment to record after `currentId`, staying inside the same
 * (batch × task group). Returns null when the group is finished.
 */
export function nextInGroup(
  items: readonly AssignmentItem[],
  currentId: string,
): AssignmentItem | null {
  // 《AI 正音测试》: the group is the level, walked in the authored sort_key order
  // (so a section's parts are recorded back to back).
  const current = items.find((a) => a.id === currentId) ?? null;
  const level = current ? aiTestLevelOf(current) : null;
  if (level !== null) {
    const ordered = orderAiTest(items.filter((a) => aiTestLevelOf(a) === level));
    const at = ordered.findIndex((a) => a.id === currentId);
    for (let i = at + 1; i < ordered.length; i += 1) {
      const a = ordered[i]!;
      if (a.state === "todo") return a;
    }
    return null;
  }
  const group = groupAssignments(items).find((g) => g.items.some((a) => a.id === currentId));
  if (!group) return null;
  const idx = group.items.findIndex((a) => a.id === currentId);
  // Forward only: anything left behind stays reachable through 查看清单.
  for (let i = idx + 1; i < group.items.length; i += 1) {
    const a = group.items[i]!;
    if (a.state === "todo") return a;
  }
  return null;
}
